/**
 * Phase 8 T-22 §27 — Generic Assistant intake stub page.
 *
 * Used by every §27.B route whose "real" wizard / page has NOT been
 * built yet in Phase 8 (e.g. `/jobs/new`, `/sequences/new`,
 * `/interviews/new`, `/workflows/new`, `/role-fit/new`,
 * `/single-optimize/new`, `/bulk/:action`, `/reports/ask`).
 *
 * Scope is intentionally UI-only:
 *   - Render `<AssistantIntake>` with the config row for the current route.
 *   - On submit: log the payload + create an agent thread via
 *     `useAgentThread` so the thread persistence path is exercised.
 *   - Route-specific actions (open wizard step 2, apply filter, open
 *     compose) slot in as the upstream wizards land.
 */

import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { AssistantIntake } from './AssistantIntake';
import { useAssistantIntakeConfig } from '../hooks/useAssistantIntakeConfig';
import { useAgentThread } from '../hooks/useAgentThread';
import { useAuth } from '@core/auth/useAuth';
import { PageHeader } from '@shared/components/ui/PageHeader';

function firstNameOf(user: { name?: string | null; email?: string | null } | null): string {
  const name = (user?.name ?? '').trim();
  if (name) {
    const first = name.split(/\s+/)[0];
    if (first && first.length > 0) return first;
  }
  const email = (user?.email ?? '').trim();
  if (email) {
    const handle = email.split('@')[0];
    if (handle) return handle.charAt(0).toUpperCase() + handle.slice(1);
  }
  return 'there';
}

/**
 * Mount point for any §27.B route whose downstream action is still being
 * built. Guarded by `useAssistantIntakeConfig` so the allowlist is
 * honoured automatically.
 */
export function AssistantIntakeStubPage() {
  const cfg = useAssistantIntakeConfig();
  const { user } = useAuth();
  const params = useParams();

  const firstName = useMemo(() => firstNameOf(user), [user]);

  // Minimal stub thread — keyed per entity so repeat intakes append.
  const scopeId = cfg?.entity ?? 'unknown';
  const { send } = useAgentThread({ scope: 'page', scopeId });

  const handleSubmit = useCallback(
    (text: string, scope: string | null) => {
      // eslint-disable-next-line no-console
      console.info('[AssistantIntake] submit', {
        route: cfg?.route,
        entity: cfg?.entity,
        scope,
        prompt: text,
      });
      void send(text).catch(() => {
        /* best-effort — the agent runner (C-41) owns retries */
      });
    },
    [send, cfg?.entity, cfg?.route],
  );

  if (!cfg) {
    return (
      <div className="flex min-h-0 flex-1 flex-col p-6">
        <PageHeader variant="minimal" breadcrumb={[{ label: 'Not available on this route' }]} />
        <p className="mt-4 text-sm vscode-text-secondary">
          The Assistant intake is not configured for this route.
        </p>
      </div>
    );
  }

  // Render "{N} selected" in the greeting for /bulk/:action when we know it.
  const greeting = cfg.greeting.replace('{N}', (params.action ? '1' : '0'));

  return (
    <AssistantIntake
      greeting={greeting}
      placeholder={cfg.placeholder}
      scopePill={cfg.scopePill}
      suggestions={cfg.suggestions}
      beta={cfg.beta}
      entityPlural={cfg.entityPlural}
      firstName={firstName}
      gradient="intake"
      onSubmit={handleSubmit}
    />
  );
}
